import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

import { ContentBlock, DisplayContentAssignment } from "./api";
import { matchesRecurrence } from "./";

dayjs.extend(utc);

/** Content types whose media is uploaded and worth caching in memory. */
const CACHEABLE_IMAGE_TYPES = new Set(["image"]);
const CACHEABLE_VIDEO_TYPES = new Set(["video", "external_video"]);

/** Recognizes a direct video file (as opposed to an embed like YouTube/Vimeo). */
const DIRECT_VIDEO_FILE = /\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)(\?.*)?$/i;

/**
 * Return the network URL of an uploaded media file that should be cached in the
 * browser (image or direct video file), or `null` for content that isn't
 * cacheable (embeds, inline data, text-based blocks, dynamic blocks).
 *
 * Only http(s) URLs are returned — inline `data:` content and third-party video
 * embeds are handled by the browser/platform and don't benefit from the cache.
 */
export const getCacheableMediaUrl = (
  contentBlock: ContentBlock | null | undefined,
): string | null => {
  if (!contentBlock) return null;

  const contentType = contentBlock.contentType?.toLowerCase();
  if (!contentType) return null;

  const source = contentBlock.fileUrl || contentBlock.contentData || "";
  if (!/^https?:\/\//i.test(source)) return null;

  if (CACHEABLE_IMAGE_TYPES.has(contentType)) {
    return source;
  }

  if (
    CACHEABLE_VIDEO_TYPES.has(contentType) &&
    DIRECT_VIDEO_FILE.test(source)
  ) {
    return source;
  }

  return null;
};

/** Collect the cacheable media URLs from a set of content blocks (de-duplicated). */
export const collectCacheableMediaUrls = (
  contentBlocks: Array<ContentBlock | null | undefined>,
): string[] => {
  const urls = new Set<string>();
  for (const block of contentBlocks) {
    const url = getCacheableMediaUrl(block);
    if (url) urls.add(url);
  }
  return Array.from(urls);
};

export const checkContentBlock = (
  localCheckTime: Dayjs,
  assignment: DisplayContentAssignment,
) => {
  // Skip if no content block
  if (!assignment.contentBlock) {
    return false;
  }

  const contentBlock = assignment.contentBlock;

  // Get effective start/end datetime (assignment overrides content block)
  const effectiveStartDatetime =
    assignment.startDatetime || contentBlock.startDatetime;
  const effectiveEndDatetime =
    assignment.endDatetime || contentBlock.endDatetime;

  // Check date/time range (startDatetime/endDatetime)
  if (effectiveStartDatetime || effectiveEndDatetime) {
    // If both startDatetime and endDatetime are not provided, show all the time
    if (!effectiveStartDatetime && !effectiveEndDatetime) {
      // Continue to other checks
    } else {
      // Check start datetime - convert UTC to local for comparison
      if (effectiveStartDatetime) {
        const startTime = dayjs.utc(effectiveStartDatetime).local();
        if (localCheckTime.isBefore(startTime)) {
          return false;
        }
      }

      // Check end datetime - convert UTC to local for comparison
      if (effectiveEndDatetime) {
        const endTime = dayjs.utc(effectiveEndDatetime).local();
        if (localCheckTime.isAfter(endTime)) {
          return false;
        }
      }
    }
  }

  // Check recurrence pattern (use local time for recurrence checks)
  if (
    contentBlock.recurrencePattern &&
    contentBlock.recurrencePattern !== "NONE"
  ) {
    if (
      !matchesRecurrence(
        localCheckTime,
        contentBlock.recurrencePattern,
        contentBlock.recurrenceDays,
      )
    ) {
      return false;
    }
  }

  // Check daily time range (startTime/endTime)
  if (contentBlock.startTime || contentBlock.endTime) {
    // Create dayjs objects for time comparison using today's date with the specified times
    const today = localCheckTime.format("YYYY-MM-DD");
    const currentTime = localCheckTime;

    let startTime: dayjs.Dayjs | undefined;
    let endTime: dayjs.Dayjs | undefined;

    if (contentBlock.startTime) {
      // Convert UTC time to local time and create a dayjs object for today
      const startTimeUTC = dayjs.utc(contentBlock.startTime);
      const startTimeLocal = startTimeUTC.local().format("HH:mm");
      startTime = dayjs(`${today} ${startTimeLocal}`);
    }

    if (contentBlock.endTime) {
      // Convert UTC time to local time and create a dayjs object for today
      const endTimeUTC = dayjs.utc(contentBlock.endTime);
      const endTimeLocal = endTimeUTC.local().format("HH:mm");
      endTime = dayjs(`${today} ${endTimeLocal}`);
    }

    // If only startTime is provided
    if (startTime && !endTime) {
      if (currentTime.isBefore(startTime)) {
        return false;
      }
    }
    // If only endTime is provided
    else if (!startTime && endTime) {
      if (currentTime.isAfter(endTime)) {
        return false;
      }
    }
    // If both startTime and endTime are provided
    else if (startTime && endTime) {
      // Handle normal time range (e.g., 09:00 - 17:00)
      if (startTime.isBefore(endTime) || startTime.isSame(endTime)) {
        if (currentTime.isBefore(startTime) || currentTime.isAfter(endTime)) {
          return false;
        }
      }
      // Handle overnight time range (e.g., 22:00 - 06:00)
      else {
        // For overnight ranges, we need to check if current time is NOT in the "off" period
        // Off period is between endTime and startTime
        if (currentTime.isAfter(endTime) && currentTime.isBefore(startTime)) {
          return false;
        }
      }
    }
  }

  return true;
};
